import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import { services } from "../data/services";
import { useLanguage } from "../context/LanguageContext";

export default function Services({ compact = false }) {
  const { t, dir, lang } = useLanguage();
  return (
    <section
      id="services"
      dir={dir}
      lang={lang}
      className="relative py-20 sm:py-24 md:py-32 px-5 sm:px-8"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHead
          eyebrow={t.services.eyebrow}
          title={t.services.title}
          sub={t.services.sub}
        />

        <div
          className={`mt-16 grid grid-cols-1 gap-px bg-white/5 border border-white/5 ${
            compact ? "sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4" : "sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4"
          }`}
        >
          {services.map((s, i) => {
            const Icon = Lucide[s.icon] || Lucide.Sparkles;
            const ts = t.services.items[i];
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
                className="group relative bg-agency-gray p-6 sm:p-7 md:p-8 hover-lift overflow-hidden"
                data-testid={`service-card-${s.id}`}
              >
                <div className="absolute -right-20 -top-20 h-44 w-44 rounded-full bg-agency-gold/0 group-hover:bg-agency-gold/10 blur-3xl transition-all duration-700" />
                <div className="relative flex flex-col h-full">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-black border border-white/10 text-agency-gold group-hover:border-agency-gold/50 transition-colors">
                      <Icon size={20} />
                    </div>
                    <span className="font-mono-tech text-[11px] text-white/30">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 sm:mt-6 font-display text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                    {ts.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">
                    {ts.short}
                  </p>
                  <ul className="mt-5 space-y-2 text-xs text-white/50">
                    {ts.bullets.slice(0, 3).map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <span className="h-px w-4 bg-agency-gold/60" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function SectionHead({ eyebrow, title, sub, align = "left" }) {
  return (
    <div
      className={`flex flex-col gap-4 ${
        align === "center" ? "items-center text-center" : ""
      }`}
    >
      <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-agency-gold">
        <span className="h-px w-8 bg-agency-gold" />
        {eyebrow}
      </div>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] max-w-3xl text-balance leading-[1.05]">
        {title}
      </h2>
      {sub && (
        <p className="max-w-2xl text-sm sm:text-base md:text-lg text-white/60 leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  );
}
